// VideoAdSDK.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { handleGameTracking } from "shared/firebase";
import { TrackingType } from "shared/constants/enum";
import { useConfig } from "../config";
import { getAdConfig as pickAdConfig } from "shared/util/adConfig";
// Global state to prevent multiple initializations
let globalSparkInitialized = false;
let globalSparkPromise = null;
let globalKalturaPromise = null;

const VideoAdPlayer = ({
  onAdCompleted = () => {},
  onNoAd = () => {},
  onError = () => {},
  type = "",
}) => {
  const { config } = useConfig();

  const { videoId, partnerId, uiConfId, siteName } = pickAdConfig(config);

  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consentStatus, setConsentStatus] = useState("checking");
  const [isInitialized, setIsInitialized] = useState(false);
  const pollingIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const adStartedRef = useRef(false);
  const kalturaPlayerRef = useRef(null);

  // TCF API implementation
  const setupTCFAPI = useCallback(() => {
    if (window.__tcfapi) {
      return; // Already set up
    }

    const queue = [];
    let win = window;
    let cmpFrame;

    window.__tcfapi = function () {
      const args = Array.prototype.slice.call(arguments);
      queue.push(args);
      if (typeof args[2] === "function") {
        postMessageToParent(args);
      }
    };

    function findCMPFrame() {
      let found = false;
      while (!found && win) {
        try {
          if (win.__tcfapi && win.__tcfapi instanceof Function) {
            found = true;
            break;
          }

          if (win.frames["__tcfapiLocator"]) {
            cmpFrame = win;
            found = true;
            break;
          }

          if (win.parent === win) {
            break;
          }
          win = win.parent;
        } catch (e) {
          break;
        }
      }
      return found;
    }

    function postMessageToParent(args) {
      const callId = Math.random().toString();
      const msg = {
        __tcfapiCall: {
          command: args[0],
          version: args[1],
          callId: callId,
        },
      };

      if (args[3] !== undefined) {
        msg.__tcfapiCall.parameter = args[3];
      }

      function handler(event) {
        if (
          event.data &&
          event.data.__tcfapiReturn &&
          event.data.__tcfapiReturn.callId === callId
        ) {
          if (typeof args[2] === "function") {
            args[2](
              event.data.__tcfapiReturn.returnValue,
              event.data.__tcfapiReturn.success
            );
          }
          window.removeEventListener("message", handler);
        }
      }

      window.addEventListener("message", handler, false);

      if (cmpFrame) {
        cmpFrame.postMessage(msg, "*");
      } else {
        window.parent.postMessage(msg, "*");
      }
    }

    function receiveMessage(event) {
      if (event.data && event.data.__tcfapiReturn) {
        let i = queue.length;
        while (i--) {
          const args = queue[i];
          const callId = event.data.__tcfapiReturn.callId;

          if (
            args[0] === event.data.__tcfapiReturn.command &&
            args[1] === event.data.__tcfapiReturn.version
          ) {
            queue.splice(i, 1);
            if (typeof args[2] === "function") {
              args[2](event.data.__tcfapiReturn.returnValue, true);
            }
          }
        }
      }
    }

    if (!findCMPFrame()) {
      console.log("No CMP-Frame found. __tcfapi as Stub called.");
    }

    window.addEventListener("message", receiveMessage, false);
  }, []);

  // Consent management
  const hasRequiredConsent = useCallback((tcData) => {
    return (
      tcData &&
      tcData.vendor &&
      tcData.vendor.consents &&
      tcData.vendor.consents[755]
    );
  }, []);

  const pollForConsent = useCallback(
    (resolve, reject) => {
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      let attempts = 0;
      const maxAttempts = 30; // 30 seconds timeout

      const checkConsent = () => {
        attempts++;

        if (typeof window.__tcfapi === "function") {
          window.__tcfapi("getTCData", 2, function (tcData, success) {
            if (!success) {
              if (attempts >= maxAttempts) {
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
                reject(new Error("Consent check failed"));
              }
              return;
            }

            if (hasRequiredConsent(tcData)) {
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              resolve(tcData);
            } else if (tcData.eventStatus === "useractioncomplete") {
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              reject(new Error("User denied consent"));
            } else if (attempts >= maxAttempts) {
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              reject(new Error("Consent timeout"));
            }
          });
        } else if (attempts >= maxAttempts) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          reject(new Error("TCF API not available"));
        }
      };

      checkConsent();
      pollingIntervalRef.current = setInterval(checkConsent, 1000);
    },
    [hasRequiredConsent]
  );

  // Load Spark SDK
  const loadSparkSDK = useCallback(() => {
    // Return existing promise if already loading
    if (globalSparkPromise) {
      return globalSparkPromise;
    }

    globalSparkPromise = new Promise((resolve, reject) => {
      if (window.Spark) {
        resolve(window.Spark);
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector(
        'script[src*="spark.cloud.funkedigital.de"]'
      );
      if (existingScript) {
        // Wait for existing script to load
        existingScript.addEventListener("load", () => {
          if (window.Spark) {
            resolve(window.Spark);
          } else {
            reject(new Error("Spark SDK failed to load"));
          }
        });
        existingScript.addEventListener("error", () =>
          reject(new Error("Failed to load Spark SDK"))
        );
        return;
      }

      const script = document.createElement("script");
      script.src = "https://spark.cloud.funkedigital.de/spark.js";
      script.onload = () => {
        if (window.Spark) {
          resolve(window.Spark);
        } else {
          reject(new Error("Spark SDK failed to load"));
        }
      };
      script.onerror = () => reject(new Error("Failed to load Spark SDK"));
      document.head.appendChild(script);
    });

    return globalSparkPromise;
  }, []);

  // Load Kaltura SDK
  const loadKalturaSDK = useCallback(() => {
    // Return existing promise if already loading
    if (globalKalturaPromise) {
      return globalKalturaPromise;
    }

    globalKalturaPromise = new Promise((resolve, reject) => {
      if (window.KalturaPlayer) {
        resolve(window.KalturaPlayer);
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector(
        `script[src*="front.vtv.funkedigital.de/p/${partnerId}/embedPlaykitJs"]`
      );
      if (existingScript) {
        // Wait for existing script to load
        existingScript.addEventListener("load", () => {
          if (window.KalturaPlayer) {
            resolve(window.KalturaPlayer);
          } else {
            reject(new Error("Kaltura SDK failed to load"));
          }
        });
        existingScript.addEventListener("error", () =>
          reject(new Error("Failed to load Kaltura SDK"))
        );
        return;
      }

      const script = document.createElement("script");
      script.src = `https://front.vtv.funkedigital.de/p/${partnerId}/embedPlaykitJs/uiconf_id/${uiConfId}`;
      script.async = true;
      script.onload = () => {
        if (window.KalturaPlayer) {
          resolve(window.KalturaPlayer);
        } else {
          reject(new Error("Kaltura SDK failed to load"));
        }
      };
      script.onerror = () => reject(new Error("Failed to load Kaltura SDK"));
      document.head.appendChild(script);
    });

    return globalKalturaPromise;
  }, [partnerId, uiConfId]);

  // Initialize Spark
  const initializeSpark = useCallback(async (Spark) => {
    if (globalSparkInitialized) {
      return Spark;
    }

    return new Promise((resolve, reject) => {
      Spark.cmd.push(function () {
        Spark.init({
          cmp: "0",
          siteName:
            window.innerWidth > 600 ? siteName.desktop : siteName.mobile,
          zoneName: "other",
          targetings: {
            environment: window.innerWidth > 600 ? "sta" : "mob",
            artikeltyp: "news",
            loktitel: "ratefuchs",
          },
        });
      });

      Spark.cmd.push(function () {
        Spark.start();
      });

      Spark.cmd.push(function () {
        Spark.addEventListener("permutiveReady", function (e) {
          if (
            (e.eventStatus === "useractioncomplete" ||
              e.eventStatus === "tcloaded") &&
            e.vendor.consents[755] === true
          ) {
            Spark.definitionService.initHbInstream();
            Spark.addEventListener("hbInstreamReady", function () {
              globalSparkInitialized = true;
              resolve(Spark);
            });
          } else {
            reject(new Error("Permutive not ready or consent denied"));
          }
        });
      });
    });
  }, []);

  // Start timeout for ad
  const startAdTimeout = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (!adStartedRef.current) {
        onNoAd();
      }
    }, 10000); // 10 seconds
  }, [onNoAd]);

  // Initialize Kaltura Player
  const initializeKalturaPlayer = useCallback(
    async (Spark) => {
      try {
        const ad_call = Spark.definitionService.getPrerollRequest({
          videoID: videoId,
          videoTags: [],
          pgname: "",
          playerHeight: 480,
          playerWidth: 640,
          playerPos: "opener",
          playlist: "false",
          playlistId: videoId,
          paid: false,
          playlistcount: "0",
        });

        // Validate ad call URL
        if (!ad_call || ad_call.trim() === "") {
          onNoAd();
          return null;
        }

        const kalturaPlayer_object = {
          targetId: containerRef.current.id,
          provider: {
            partnerId: partnerId,
            uiConfId: uiConfId,
          },
          plugins: {
            ima: {
              adTagUrl: ad_call,
              adsRenderingSettings: {
                loadVideoTimeout: 3000,
                enablePreloading: false,
              },
              vpaidMode: "ENABLED",
              autoPlayAdBreaks: true,
              disableCustomPlaybackForIOS10Plus: true,
            },
          },
          player: {
            playback: {
              autoplay: true,
              preload: "auto",
              muted: false,
            },
          },
        };

        // Ensure container exists and is ready
        if (!containerRef.current) {
          throw new Error("Player container not found");
        }

        const kalturaPlayer = window.KalturaPlayer.setup(kalturaPlayer_object);

        // Start the 10-second timeout
        startAdTimeout();

        // Event listeners
        kalturaPlayer.addEventListener(kalturaPlayer.Event["PLAY"], (event) => {
          adStartedRef.current = true;
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          onNoAd();
        });

        kalturaPlayer.addEventListener(
          kalturaPlayer.Event["AD_STARTED"],
          (event) => {
            adStartedRef.current = true;
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            try {
              handleGameTracking &&
                handleGameTracking(TrackingType.VideoAdStart, type);
            } catch (e) {}
          }
        );

        kalturaPlayer.addEventListener(
          kalturaPlayer.Event["AD_COMPLETED"],
          (event) => {
            adStartedRef.current = true;
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            try {
              handleGameTracking &&
                handleGameTracking(TrackingType.VideoAdComplete, type);
            } catch (e) {}
            onAdCompleted();
          }
        );

        kalturaPlayer.addEventListener(
          kalturaPlayer.Event["AD_ERROR"],
          (event) => {
            adStartedRef.current = true;
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            // On ad error, treat as no ad
            onNoAd();
          }
        );

        kalturaPlayer.addEventListener(
          kalturaPlayer.Event["ERROR"],
          (event) => {
            adStartedRef.current = true;
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            onError();
          }
        );

        // Load media after a short delay to ensure container is ready
        setTimeout(() => {
          try {
            kalturaPlayer.loadMedia({ entryId: videoId });
          } catch (loadError) {
            console.error("Failed to load media:", loadError);
            onError();
          }
        }, 100);

        kalturaPlayerRef.current = kalturaPlayer;
        return kalturaPlayer;
      } catch (error) {
        console.error("Kaltura player initialization error:", error);

        // If it's an IMA-related error, treat as no ad
        if (error.message && error.message.includes("ima")) {
          onNoAd();
        } else {
          throw error;
        }
      }
    },
    [
      videoId,
      partnerId,
      uiConfId,
      onAdCompleted,
      onNoAd,
      onError,
      startAdTimeout,
    ]
  );

  // Main initialization
  useEffect(() => {
    const initializeAdPlayer = async () => {
      if (isInitialized) {
        return; // Prevent multiple initializations
      }

      try {
        setIsLoading(true);
        setError(null);

        // Setup TCF API
        setupTCFAPI();

        // Wait for consent
        setConsentStatus("checking");
        await new Promise((resolve, reject) => pollForConsent(resolve, reject));
        setConsentStatus("granted");

        // Load SDKs
        const Spark = await loadSparkSDK();
        await loadKalturaSDK();

        // Initialize Spark
        await initializeSpark(Spark);

        // Initialize Kaltura Player
        await initializeKalturaPlayer(Spark);

        setIsLoading(false);
        setIsInitialized(true);
      } catch (error) {
        console.error("Ad player initialization failed:", error);
        setError(error.message);
        setConsentStatus("denied");
        setIsLoading(false);
        onError();
      }
    };

    if (containerRef.current && !isInitialized) {
      initializeAdPlayer();
    }

    return () => {
      // Cleanup
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Properly destroy Kaltura player
      if (kalturaPlayerRef.current) {
        try {
          kalturaPlayerRef.current.destroy();
        } catch (destroyError) {
          console.error("Error destroying Kaltura player:", destroyError);
        }
        kalturaPlayerRef.current = null;
      }

      // Reset initialization flags
      setIsInitialized(false);
      adStartedRef.current = false;

      // Note: Don't reset globalSparkInitialized here as it should persist across instances
      // Only reset if this is the last instance (you might want to add a counter if needed)
    };
  }, []); // Empty dependency array to prevent infinite loops

  // Generate unique container ID
  useEffect(() => {
    if (containerRef.current && !containerRef.current.id) {
      containerRef.current.id = `kaltura_player_${Math.random().toString(36).substr(2, 9)}`;
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isInitialized || isLoading) {
        setError("Ad loading timed out");
        setIsLoading(false);
        onError();
      }
    }, 40000); // 40 seconds

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        height: "100vh",
        width: "100vw",
        background: "#222d",
        backdropFilter: "blur(5px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2,
      }}
    >
      <div
        ref={containerRef}
        style={{
          height: 1500,
          maxHeight: "90%",
          maxWidth: "90%",
          aspectRatio: 1.6,
          background: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isLoading && (
          <div style={{ color: "white", textAlign: "center" }}>
            {consentStatus === "checking" && "Checking consent..."}
            {consentStatus === "granted" && "Loading video player..."}
            {consentStatus === "denied" && "Consent required to show ads"}
          </div>
        )}
        {error && (
          <div style={{ color: "red", textAlign: "center" }}>
            Error: {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoAdPlayer;
